import { ProductCategoryKeys } from '../common/enums/common';
import { TyConfig } from '../providers/config-provider';
import { ERegionBasic } from '../types';
import { getCommerceToolsCategoryId, getCommerceToolsChannelId } from './configUtils';
import { getKPRegion } from './regionUtils';

// Enum for WEB_ENABLED values
export enum WebEnabled {
  NOT_AVAILABLE = 0,
  VISIBLE_SELLABLE = 1,
  VISIBLE_NOT_SELLABLE = 2,
  ERROR = 3,
}

/**
 * Replace category ENUMs with CommerceTools category IDs.
 *
 * @param categories - The array of category IDs to replace.
 * @returns The array with replaced category IDs.
 */

const replaceWithCategoryIds = (config: any, categories: ProductCategoryKeys[]): string[] => {
  return categories.map(categoryEnum => getCommerceToolsCategoryId(config, categoryEnum));
};

export interface CustomFilters {
  channelId?: string;
  categories?: ProductCategoryKeys[];
}

export interface CustomFilters {
  channelId?: string;
  categories?: ProductCategoryKeys[];
}

export interface AdditionalAvailableFilters {
  allowReleaseDateEnabledFilter: boolean;
  allowWebEnabledFilter: boolean;
  webEnabledSellableOnly?: boolean;
}

/**
 * Get common product filters based on region and configuration.
 *
 * @param kpConfigs - The configuration object.
 * @param queryFilters - The array to push filters into.
 * @param channelId - Optional channel ID to use for filtering.
 * @param categories - Optional array of category IDs to filter by.
 */
export const getCommonProductFilters = (
  kpConfigs: TyConfig,
  queryFilters: any[],
  customFilters?: CustomFilters,
  additionalAvailableFilters: AdditionalAvailableFilters = {
    allowReleaseDateEnabledFilter: true,
    allowWebEnabledFilter: true,
  },
): void => {
  const region = getKPRegion();
  const regionLowerCase = region?.toLowerCase() as keyof TyConfig;
  const baseFilters = [];

  const hasReleaseDate = queryFilters?.some(filter => {
    return filter?.string?.includes('variants.attributes.RELEASE_DATE');
  });

  // Check to see if contact lenses
  // Check if any category string contains 'contact' or 'lens'/'lenses'
  let isContactLenses = false;
  if (customFilters?.categories?.length) {
    isContactLenses = customFilters.categories.some(cat => {
      const parts = cat.split(/[\._]/).map(p => p.toLowerCase());
      return parts.includes('contact') || parts.includes('lens') || parts.includes('lenses');
    });
  }

  // Now you can use contactLensParts to get 'contact', 'lenses', or 'basePage' as needed
  // Default use region Prices Channel otherwise use parameter passed channel
  if (customFilters?.channelId?.trim().length) {
    baseFilters.push({ string: `variants.prices.channel.id: "${customFilters.channelId}"` });
  } else if (kpConfigs?.[regionLowerCase] !== undefined) {
    // const regionChannel = getConfigAsStringByKey(REGION_MAP_FRAMES_CHANNEL[region], kpConfigs, false);
    const regionChannel = getCommerceToolsChannelId(kpConfigs);
    baseFilters.push({ string: `variants.prices.channel.id: "${regionChannel}"` });
  }

  if (!kpConfigs) {
    return;
  }

  // Add filters based on configuration
  if (
    additionalAvailableFilters.allowWebEnabledFilter &&
    kpConfigs[regionLowerCase]?.commerceTools?.isWebEnabled
  ) {
    if (additionalAvailableFilters.webEnabledSellableOnly || isContactLenses) {
      baseFilters.push({
        string: `variants.attributes.WEB_ENABLED.key:"${WebEnabled.VISIBLE_SELLABLE}"`,
      });
    } else {
      baseFilters.push({
        string: `variants.attributes.WEB_ENABLED.key:"${WebEnabled.VISIBLE_SELLABLE}","${WebEnabled.VISIBLE_NOT_SELLABLE}"`,
      });
    }
  }
  if (kpConfigs[regionLowerCase]?.commerceTools?.isProductAvailable) {
    baseFilters.push({ string: 'variants.attributes.IS_AVAILABLE:"true"' });
  }
  if (
    !hasReleaseDate &&
    additionalAvailableFilters.allowReleaseDateEnabledFilter &&
    !isContactLenses &&
    (kpConfigs[regionLowerCase]?.commerceTools?.releaseDateCheckEnabled ?? false)
  ) {
    const now = new Date();
    const formattedNow = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    baseFilters.push({ string: `variants.attributes.RELEASE_DATE:range(* to "${formattedNow}")` });
  }

  // Add category filters if provided
  if (customFilters?.categories?.length) {
    const categoryIds = replaceWithCategoryIds(kpConfigs, customFilters.categories);
    const categoriesString = categoryIds.join('","');
    baseFilters.push({ string: `categories.id:"${categoriesString}"` });
  }
  // Add base filters to query filters
  queryFilters.push(...baseFilters);
};
